import type { Ref } from '@vue/composition-api';
import { ref, computed, watch } from '@vue/composition-api';
import { validateEmail } from '@getaccept/lib-shared/src/helpers';
import { IdentifyRecipientHelper } from '../../../universal-link/helpers/identify-recpient.helper';
import type { VerifyLinkResponse } from '../../../api/universal-link/types/verify-link-response';
import type { IdentifyRecipientPayload } from '../../../api/universal-link/types/identify-recipient-payload';
import { useUniversalLink } from './universal-link.composable';

export function useIdentifyRecipient(verifyLinkResponse: VerifyLinkResponse) {
  const { loadingRedirectURL, getRedirectUrl, redirectURLError } = useUniversalLink();
  const emailInvalid: Ref<boolean> = ref(false);
  const consentGiven: Ref<boolean> = ref(false);
  const recipient: Ref<IdentifyRecipientPayload> = ref<IdentifyRecipientPayload>({
    email: '',
    firstName: '',
    lastName: '',
    title: '',
    companyName: '',
  });

  const submitDisabled = computed(
    () =>
      emailInvalid.value ||
      IdentifyRecipientHelper.isSubmitDisabled(
        recipient.value.email,
        consentGiven.value,
        verifyLinkResponse.requireGdprConsent
      )
  );

  const errorMessage = computed(() =>
    IdentifyRecipientHelper.getErrorMessage(redirectURLError.value)
  );

  const helpMessage = computed(() => IdentifyRecipientHelper.getHelpMessage(emailInvalid.value));

  const consentStep = computed(() =>
    IdentifyRecipientHelper.getConsentStep({
      consentText: verifyLinkResponse.consentText,
      entityName: verifyLinkResponse.entityName,
    })
  );

  const submit = () => {
    if (!validateEmail(recipient.value?.email?.toLowerCase())) {
      emailInvalid.value = true;
      return;
    }
    getRedirectUrl(consentGiven.value, verifyLinkResponse.documentId, recipient.value);
  };

  watch(recipient.value, () => {
    redirectURLError.value = '';
    if (emailInvalid.value) {
      emailInvalid.value = false;
    }
  });

  const setConsent = (given: boolean) => {
    consentGiven.value = given;
  };
  return {
    loadingRedirectURL,
    submitDisabled,
    submit,
    helpMessage,
    emailInvalid,
    errorMessage,
    recipient,
    consentGiven,
    setConsent,
    consentStep,
  };
}
